import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";
import { ActiveItemTrending } from "./ActiveItemTrending";
import { AmountInCustomerCurrency } from "./AmountInCustomerCurrency";
import HomePageCard from "./HomePageCard";
import { type LocationState } from "./types";

type DefaultProps = {
  title: string | React.ReactNode;
  cloudCostsAtRisk: number;
  dataIcon: "trending up" | "trending down" | "warning" | "suggestion";
  trend: number | string;
  trendColor?: string;
  trendDate?: string;
  value: number;
  buttonLink?: string;
  riskLabel?: string;
  riskIsNumber?: boolean;
  dontConvert?: boolean;
  compactFormat?: boolean;
  disabled?: boolean;
  trackingEvent?: string;
  trackingDescription?: string;
  locationState?: LocationState;
};

const ActiveItemsCard = ({
  title,
  value,
  cloudCostsAtRisk,
  dataIcon,
  trend,
  trendColor,
  trendDate,
  buttonLink,
  riskLabel = "Cloud costs at risk",
  riskIsNumber = false,
  dontConvert = false,
  compactFormat = false,
  disabled = false,
  trackingEvent,
  trackingDescription,
  locationState,
}: DefaultProps) => {
  const history = useHistory();
  const { trackEvent } = useSegmentTrackEvent();

  const handleButtonClick = () => {
    // Track the event if tracking props are provided
    if (trackingEvent && trackingDescription) {
      trackEvent(trackingEvent, {
        source: "Home Dashboard",
        feature: "Home Dashboard",
        target_internal_destination_description: trackingDescription,
      });
    }

    if (buttonLink) {
      history.push(buttonLink, locationState);
    }
  };

  return (
    <HomePageCard title={title} sxProps={{ height: "100%" }}>
      <Stack sx={{ p: 3, height: 177 }} justifyContent="space-between">
        <Stack alignItems="flex-start" spacing={1}>
          <Stack direction="row" alignItems="center">
            <Typography sx={{ fontSize: 44, fontWeight: 500, lineHeight: "44px" }}>{value}</Typography>
            <ActiveItemTrending trend={trend} trendColor={trendColor} trendDate={trendDate} dataIcon={dataIcon} />
          </Stack>
          {cloudCostsAtRisk > 0 && (
            <Stack direction="row" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {riskLabel}:
              </Typography>
              <Typography variant="h3" color="text.primary" sx={{ ml: 1 }}>
                {riskIsNumber ? (
                  cloudCostsAtRisk
                ) : (
                  <AmountInCustomerCurrency
                    amount={cloudCostsAtRisk}
                    dontConvert={dontConvert}
                    compactFormat={compactFormat}
                  />
                )}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Tooltip
          title={
            disabled ? "You don't have the correct permissions to perform this action. Please contact your admin." : ""
          }
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={disabled}
              sx={{ alignSelf: "flex-start", "@media print": { display: "none" } }}
              data-cy="anomalies-btn"
              endIcon={<ArrowForwardIcon />}
              onClick={handleButtonClick}
            >
              Review and take action
            </Button>
          </span>
        </Tooltip>
      </Stack>
    </HomePageCard>
  );
};

export default ActiveItemsCard;
